import type { Commerce, CommerceConfiguration } from "@teqtank/commerce-contracts";
import { resolveCommerceConfiguration } from "../configuration/resolve-commerce-configuration.js";
import { bootstrapCommerceContext } from "../context/bootstrap-commerce-context.js";
import { bootstrapCommerceSession } from "../session/bootstrap-commerce-session.js";
import { createCorrelationId } from "../utilities/create-correlation-id.js";
import { CommerceRuntime } from "./commerce-runtime.js";
import { RuntimeBootstrapRecorder } from "./runtime-bootstrap-recorder.js";

export async function bootstrapCommerceRuntime(
    version: string,
    configuration: CommerceConfiguration,
): Promise<Commerce> {
    console.log("Bootstrapping Teqtank Commerce runtime:", {
        version,
        commerceKey: configuration.commerceKey,
        dataSource: configuration.dataSource,
    });
    const correlationId = createCorrelationId();
    const recorder = new RuntimeBootstrapRecorder(correlationId);

    const initialization = await recorder.measure("configuration", () =>
        resolveCommerceConfiguration(configuration),
    );

    const session = await recorder.measure("session", () =>
        bootstrapCommerceSession(
            initialization.transport,
            initialization.configuration.commerceKey,
            initialization.configuration.dataSource,
            correlationId,
        ),
    );

    const context = await recorder.measure("context", () =>
        bootstrapCommerceContext(initialization.transport, session, correlationId),
    );

    await recorder.measure("validation", () =>
        validateRuntimeConsistency(
            initialization.configuration.commerceKey,
            session.commerceKey,
            initialization.configuration.dataSource,
            session.dataSource,
        ),
    );

    console.log("Commerce runtime initialized successfully:", {
        version,
        configuration: initialization.configuration,
        context,
    });

    return new CommerceRuntime(
        version,
        initialization.configuration,
        session,
        context,
        recorder.createDiagnostics(initialization.transport.name),
        initialization.transport,
        initialization.paymentAdapter,
        initialization.operationMiddleware,
        initialization.shareProviders,
        initialization.eventListeners,
    );
}

function validateRuntimeConsistency(
    configuredCommerceKey: string,
    sessionCommerceKey: string,
    configuredDataSource: string,
    sessionDataSource: string,
): void {
    if (
        configuredCommerceKey !== sessionCommerceKey ||
        configuredDataSource !== sessionDataSource
    ) {
        throw new Error(
            "Commerce runtime initialization produced inconsistent Commerce configuration state.",
        );
    }
}
